// RecipeCookbookSide — the "cookbook" view of a single recipe,
// shown when RecipeFlipCard is flipped. Aggregates the user's
// cooking history for this slug: count, last cooked, rating
// distribution, photo gallery, and per-cook reviews/notes.
//
// Reuses the existing hooks: useRecipeCookStats (per-slug stats
// keyed by user) and useRecipePhotos (cook_log_photos +
// recipe_photos union). For per-cook reviews + notes we hit
// cook_logs directly with a tight WHERE on (user_id, recipe_slug)
// since useCookLog is single-log.

import { useEffect, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { useRecipeCookStats, relativeTimeShort } from "../lib/useRecipeCookStats";
import { useRecipePhotos } from "../lib/useRecipePhotos";

const RATING_META = {
  nailed: { emoji: "🤩", label: "Nailed it",   color: COLOR.gold },
  good:   { emoji: "😊", label: "Pretty good", color: COLOR.leaf },
  meh:    { emoji: "😐", label: "Meh",         color: COLOR.dim },
  rough:  { emoji: "😬", label: "Rough one",   color: COLOR.rose },
};

export default function RecipeCookbookSide({ recipe, userId }) {
  const slug = recipe?.slug;
  const { stats } = useRecipeCookStats(userId);
  const slugStats = stats?.get(slug) || { cookCount: 0, lastCookedAt: null, lastRating: null };
  const { photos } = useRecipePhotos(slug, userId);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!userId || !slug) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("cook_logs")
        .select("id, rating, notes, cooked_at, xp_earned")
        .eq("user_id", userId)
        .eq("recipe_slug", slug)
        .order("cooked_at", { ascending: false })
        .limit(20);
      if (cancelled) return;
      if (error) {
        console.warn("[cookbook-side] logs:", error.message);
        setLogs([]);
      } else {
        setLogs(data || []);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, slug]);

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        padding: "24px 20px calc(76px + env(safe-area-inset-bottom))",
      }}
    >
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: "0.14em",
          color: COLOR.sub,
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        COOKBOOK
      </div>
      <div
        style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 28,
          color: COLOR.ink,
          marginBottom: 16,
          lineHeight: 1.15,
        }}
      >
        {recipe?.recipe?.title || recipe?.title || "Untitled"}
      </div>

      {/* Quick stats row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
          marginBottom: 20,
        }}
      >
        <StatTile
          big={slugStats.cookCount}
          label={slugStats.cookCount === 1 ? "TIME COOKED" : "TIMES COOKED"}
        />
        <StatTile
          big={slugStats.lastCookedAt ? relativeTimeShort(slugStats.lastCookedAt) : "—"}
          label="LAST COOKED"
        />
        <StatTile
          big={slugStats.lastRating ? RATING_META[slugStats.lastRating]?.emoji : "—"}
          label={slugStats.lastRating ? RATING_META[slugStats.lastRating]?.label.toUpperCase() : "NO RATING"}
        />
      </div>

      {/* Photo gallery */}
      {photos.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <SectionLabel label="Photos" />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
              gap: 6,
            }}
          >
            {photos.slice(0, 8).map((p) => (
              <div
                key={p.storage_path}
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  background: `url(${p.url}) center / cover no-repeat`,
                  borderRadius: 8,
                  border: `1px solid ${COLOR.border}`,
                }}
                aria-label="cooked photo"
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-cook history */}
      <SectionLabel label="Cooks" />
      {logs.length === 0 ? (
        <div
          style={{
            padding: 16,
            background: COLOR.soil,
            border: `1px dashed ${COLOR.border}`,
            borderRadius: 12,
            textAlign: "center",
            fontFamily: FONT.forma,
            fontSize: 13,
            color: COLOR.muted,
          }}
        >
          No cooks logged yet. Tap to cook → it'll show up here when you finish.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {logs.map((log) => {
            const meta = RATING_META[log.rating] || null;
            return (
              <div
                key={log.id}
                style={{
                  padding: 12,
                  background: "#fff",
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {meta && <span style={{ fontSize: 22 }}>{meta.emoji}</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: FONT.forma, fontSize: 14, fontWeight: 600, color: COLOR.ink }}>
                      {meta?.label || "Cooked"}
                    </div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.06em" }}>
                      {relativeTimeShort(log.cooked_at ? +new Date(log.cooked_at) : null) || "—"}
                    </div>
                  </div>
                  {log.xp_earned ? (
                    <div
                      style={{
                        padding: "4px 10px",
                        background: COLOR.goldDeep,
                        borderRadius: 999,
                        fontFamily: FONT.mono,
                        fontSize: 10,
                        letterSpacing: "0.08em",
                        color: COLOR.gold,
                        fontWeight: 700,
                      }}
                    >
                      +{log.xp_earned} XP
                    </div>
                  ) : null}
                </div>
                {log.notes && (
                  <div
                    style={{
                      fontFamily: FONT.forma,
                      fontSize: 13,
                      color: COLOR.ink,
                      lineHeight: 1.45,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {log.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatTile({ big, label }) {
  return (
    <div
      style={{
        padding: "14px 10px",
        background: "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 12,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 22,
          color: COLOR.ink,
          lineHeight: 1.1,
          marginBottom: 4,
        }}
      >
        {big ?? "—"}
      </div>
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 9,
          letterSpacing: "0.12em",
          color: COLOR.sub,
          fontWeight: 700,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function SectionLabel({ label }) {
  return (
    <div
      style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        letterSpacing: "0.12em",
        fontWeight: 700,
        color: COLOR.sub,
        marginBottom: 8,
      }}
    >
      {label.toUpperCase()}
    </div>
  );
}
